/**
 * space/starfield.ts — Starfield rendering
 *
 * M2: Procedural starfield with 25k stars positioned on a distant sphere
 * in the far scene. Stars have magnitude-based brightness and B-V color.
 * Includes a subtle milky-way band at low intensity.
 *
 * Note: For M6 (beauty pass), replace with real HYG/BSC catalog data
 * for accurate constellations. This procedural version provides a convincing
 * starfield for development and perf testing.
 */

import * as THREE from 'three';

// ── Procedural star generator ──────────────────────────────────
// Simple PRNG (mulberry32) for deterministic output
function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = (seed ^ (seed >>> 15)) * (1 | seed);
    t = (t + (t ^ (t >>> 7)) * (61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const STAR_COUNT = 25000;
const STAR_SPHERE_RADIUS = 9e6; // 9 million km

// Galactic plane parameters (J2000 galactic north pole)
const GAL_NORTH_RA = 192.8595 * (Math.PI / 180);
const GAL_NORTH_DEC = 27.1283 * (Math.PI / 180);
const GAL_CENTER_RA = 266.4051 * (Math.PI / 180); // Sagittarius A*

/** Create the starfield Points object */
export function createStarfield(): THREE.Points {
  const rng = mulberry32(42);

  const positions = new Float32Array(STAR_COUNT * 3);
  const colors = new Float32Array(STAR_COUNT * 3);
  const sizes = new Float32Array(STAR_COUNT);

  for (let i = 0; i < STAR_COUNT; i++) {
    // Generate position on unit sphere, biased toward galactic plane
    let ra: number, dec: number;

    const isMilkyWay = rng() < 0.6; // 60% stars near galactic plane

    if (isMilkyWay) {
      // Galactic disk: band ±20° around galactic equator
      // Convert: generate in galactic coords, convert to equatorial
      const galLat = (rng() * 2 - 1) * 0.35; // ±20° in radians
      const galLon = rng() * Math.PI * 2;

      // Galactic to equatorial conversion (J2000)
      const sinLat = Math.sin(galLat);
      const cosLat = Math.cos(galLat);
      const sinLon = Math.sin(galLon);
      const cosLon = Math.cos(galLon);

      // Galactic north pole in equatorial (RA, Dec)
      const sinGnpDec = Math.sin(GAL_NORTH_DEC);
      const cosGnpDec = Math.cos(GAL_NORTH_DEC);

      // Vector in equatorial
      const x = cosLat * cosLon;
      const y = cosLat * sinLon;
      const z = sinLat;

      // Rotate from galactic to equatorial (approximate)
      // This is a simplification — real conversion uses 3x3 rotation matrix
      const eqX = -x * 0.0548755 - y * 0.8734371 - z * 0.4838350;
      const eqY =  x * 0.4941094 - y * 0.4448296 + z * 0.7469822;
      const eqZ = -x * 0.8676657 - y * 0.1980764 + z * 0.4559838;

      dec = Math.asin(Math.max(-1, Math.min(1, eqZ)));
      ra = Math.atan2(eqY, eqX);
    } else {
      // Random distribution across the whole sky
      dec = Math.asin(rng() * 2 - 1);
      ra = rng() * Math.PI * 2;
    }

    // Convert to Cartesian on sphere
    const cosDec = Math.cos(dec);
    const x = cosDec * Math.cos(ra) * STAR_SPHERE_RADIUS;
    const y = Math.sin(dec) * STAR_SPHERE_RADIUS;
    const z = -cosDec * Math.sin(ra) * STAR_SPHERE_RADIUS;

    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    // Magnitude: brighter stars along galactic equator, random elsewhere
    // Magnitudes roughly 0 (brightest) to 6 (faintest visible)
    let mag: number;
    if (isMilkyWay && rng() < 0.02) {
      mag = rng() * 2.0; // bright: 0-2
    } else if (isMilkyWay) {
      mag = 2.0 + rng() * 4.0; // moderate: 2-6
    } else {
      mag = 3.0 + rng() * 4.0; // dimmer: 3-7
    }

    // Size: inversely proportional to magnitude
    sizes[i] = Math.max(0.5, 6.0 - mag) * 2000;

    // Color from B-V index (approximate)
    // Hot stars (blue-white): B-V ~ -0.3 to 0.0
    // Sun-like: B-V ~ 0.65
    // Cool stars (orange-red): B-V ~ 1.0 to 1.5
    const bv = (rng() * 2 - 0.3); // -0.3 to 1.7

    // B-V to sRGB color (approximate)
    let cr: number, cg: number, cb: number;
    if (bv < 0.0) {
      // Blue-white
      cr = 0.8 + bv * 0.5;
      cg = 0.85 + bv * 0.5;
      cb = 1.0;
    } else if (bv < 0.6) {
      // White to yellow
      const t = bv / 0.6;
      cr = 1.0;
      cg = 0.85 + t * 0.15;
      cb = 1.0 - t * 0.35;
    } else {
      // Orange to red
      const t = Math.min(1, (bv - 0.6) / 1.0);
      cr = 1.0;
      cg = 1.0 - t * 0.4;
      cb = 0.65 - t * 0.4;
    }

    colors[i * 3] = cr;
    colors[i * 3 + 1] = cg;
    colors[i * 3 + 2] = cb;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  // Use PointsMaterial with size attenuation for realistic star rendering
  const mat = new THREE.PointsMaterial({
    size: 5000,
    vertexColors: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    transparent: true,
    opacity: 0.9,
    sizeAttenuation: true,
  });

  const points = new THREE.Points(geo, mat);
  return points;
}

/**
 * Create a simple milky way band as a transparent ring on the far sphere.
 * For M2, this is a subtle glow band; M6 can upgrade to an equirect texture.
 */
export function createMilkyWayGlow(): THREE.Mesh {
  // A faint ring-like glow around the galactic equator
  const geo = new THREE.TorusGeometry(STAR_SPHERE_RADIUS * 0.95, STAR_SPHERE_RADIUS * 0.3, 16, 100);
  const mat = new THREE.MeshBasicMaterial({
    color: 0x334466,
    transparent: true,
    opacity: 0.03,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const torus = new THREE.Mesh(geo, mat);

  // Orient torus to galactic plane
  // Galactic north pole in equatorial
  torus.rotation.x = -(Math.PI / 2 - GAL_NORTH_DEC);
  torus.rotation.z = GAL_NORTH_RA - Math.PI / 2;

  return torus;
}
